import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import {
  AdCampaignData,
  FacebookPage,
  Location,
} from '@promoboxx/graphql-gateway-types'
import { FormikContext } from 'formik'
import I18n from 'i18n-js'
// import PropTypes from 'prop-types'
import { memo, useMemo } from 'react'
import { useHistory, useParams } from 'react-router'

import retailerSettingsPaths from '@src/components/retailerSettings/retailerSettingsPaths'
import AppConfig from '@src/config/AppConfig'
import formikConnect from '@src/lib/formikConnect'
import { useFeatureFlag } from '@src/lib/useFeatureFlag'

import { createR1UrlBasePath } from '../../../lib/Utils'

import PaidAdCTAActivate from './PaidAdCTAActivate'
import ManageFacebookButton from './PaidAdCTAManageFacebook'

const PaidAdCTA = ({
  facebookPages,
  formik,
  campaign,
  disabled,
  locations,
  facebookPageId,
}: {
  facebookPages: FacebookPage[]
  formik: FormikContext<any>
  campaign: AdCampaignData
  disabled: boolean
  locations: Location[]
  facebookPageId: string
}) => {
  const params = useParams() as {
    retailerAccountSlug: string
    brandSlug: string
  }
  const hasSelectedFacebookPage =
    facebookPages.length > 0 && formik?.values.facebookPageId
  let currentPageHasBusinessPermissions = false

  const page = facebookPages.find((page) => page.id === facebookPageId)
  if (page?.business_manager_permissions) {
    currentPageHasBusinessPermissions = true
  }
  const isNewBusinessSettingsEnabled = useFeatureFlag('new-business-settings')
  const history = useHistory()

  // Notice!!
  // This overrires the need to be paid ready in dev/local. Change it for your needs...
  const DEBUG_BYPASS_PAID_AD_READY_FPPAP =
    AppConfig.ENV === 'development' || AppConfig.ENV === 'local'

  const showManageFacebookConnection = useMemo(() => {
    // No page? Show the button
    if (!hasSelectedFacebookPage) {
      return true
    }
    // Are we in DEV/LOCAL? Hide the button
    if (DEBUG_BYPASS_PAID_AD_READY_FPPAP) {
      return false
    }
    // We have a Page selected, but it's not Paid ready? Show the button
    if (hasSelectedFacebookPage && !currentPageHasBusinessPermissions) {
      return true
    }
    return false
  }, [
    DEBUG_BYPASS_PAID_AD_READY_FPPAP,
    currentPageHasBusinessPermissions,
    hasSelectedFacebookPage,
  ])

  if (showManageFacebookConnection) {
    // TODO Why does this component care about facebook pages?
    // In the actual UI, it is underneath a section literally devoted to
    // facebook pages.
    // TODO It's just a button. No need to be its own component.
    return (
      <>
        {hasSelectedFacebookPage && !currentPageHasBusinessPermissions && (
          <Alert severity="warning">
            {I18n.t('content.selectePageNoPermission')}
          </Alert>
        )}
        <ManageFacebookButton
          onClick={() => {
            if (isNewBusinessSettingsEnabled) {
              history.push(
                retailerSettingsPaths.socialAccounts.build({
                  retailerAccountSlug: params.retailerAccountSlug,
                }),
              )
            } else {
              window.location.replace(
                `/#${createR1UrlBasePath(
                  params.retailerAccountSlug,
                  params.brandSlug,
                )}settings?facebook_perm_modal=true&tab=social_accounts`,
              )
            }
          }}
        />
      </>
    )
  } else if (locations.length === 0) {
    return (
      <>
        <Typography color="textSecondary" paragraph variant="body2">
          {I18n.t('content.premiumMissingLocationsDescription')}
        </Typography>
        <Button
          color="primary"
          fullWidth
          onClick={() => {
            window.location.replace(
              `/#${createR1UrlBasePath(
                params.retailerAccountSlug,
                params.brandSlug,
              )}settings?tab=location`,
            )
          }}
          size="large"
          variant="contained"
        >
          {I18n.t('content.premiumMissingLocationsButtonLabel')}
        </Button>
      </>
    )
  } else {
    return (
      <>
        <PaidAdCTAActivate
          campaign={campaign}
          /* TODO Why is `formik.isValid` false when there are no errors ... */
          disabled={Object.keys(formik?.errors || []).length > 0 || disabled}
          isLoading={formik?.isSubmitting}
          onClick={formik?.submitForm}
        />
      </>
    )
  }
}

export default formikConnect(memo(PaidAdCTA))
